import React, { useState, useEffect, useCallback } from "react";
import { Box } from "@mui/material";
import { Button } from "@mui/material";
import SbuxTextField from "../../../../../components/SubxTextField";
import SbuxStackSnackbar from "../../../../../components/SbuxStackSnackbar";
import {
  createOrUpdateIvrTable,
  setStatusUpdate,
} from "../../../../../services/ivrTable";
import styles from "../../styles";
import useCss from "../../../../../hooks/useCss";
import {
  getIvrTableStatusCodeSelector,
  getIvrTableMessageSelector,
} from "../../../../../selectors/ivrTableSelector";
import SbuxTranslate from "../../../../../components/SbuxTranslate";
import { useAppDispatch, useAppSelector } from "../../../../../store/types";

const initTable = { tableName: "", partitionKey: "", sortKey: "" };

const AdminIvrTable = () => {
  const classes = useCss(styles);
  const dispatch = useAppDispatch();
  const [table, setTable] = useState(initTable);
  const [tableNameError, setTableNameError] = React.useState(false);
  const [partitionKeyError, setPartitionKeyError] = React.useState(false);
  const [openSnackbar, setOpenSnackbar] = React.useState(false);

  const statusCode = useAppSelector(getIvrTableStatusCodeSelector);
  const message = useAppSelector(getIvrTableMessageSelector) as string | null;

  const handleClear = useCallback(() => {
    setTable(initTable);
    setTableNameError(false);
    setPartitionKeyError(false);
  }, []);

  useEffect(() => {
    if (
      (statusCode === 200 || statusCode === 400) &&
      message &&
      message.length > 0
    ) {
      setOpenSnackbar(true);
      if (statusCode === 200) {
        handleClear();
      }
    }
  }, [statusCode, message, handleClear]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setTable({ ...table, [name]: value });
    name === "tableName"
      ? setTableNameError(value.length < 1 ? true : false)
      : name === "partitionKey" &&
        setPartitionKeyError(value.length < 1 ? true : false);
  };
  const handleSubmit = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    if (table.tableName && table.partitionKey) {
      dispatch(createOrUpdateIvrTable(table));
    }
  };
  const handleClose = () => {
    setOpenSnackbar(false);
    dispatch(setStatusUpdate());
  };

  return (
    <>
      <Box className={classes.formContainer}>
        <Box
          component="form"
          className={classes.formControl}
          noValidate
          autoComplete="off"
        >
          <Box>
            <SbuxTextField
              label={<SbuxTranslate>New Table Name</SbuxTranslate>}
              handleChange={handleChange}
              value={table.tableName}
              name={"tableName"}
              isError={tableNameError}
              required
              focused
              errorMessage={
                tableNameError ? (
                  <SbuxTranslate>
                    New Table Name required at least 1 character
                  </SbuxTranslate>
                ) : (
                  ""
                )
              }
            />
          </Box>

          <Box>
            <SbuxTextField
              label={<SbuxTranslate>Partition Key</SbuxTranslate>}
              handleChange={handleChange}
              value={table.partitionKey}
              name={"partitionKey"}
              isError={partitionKeyError}
              required
              preventAutoFocus
              errorMessage={
                partitionKeyError ? (
                  <SbuxTranslate>
                    Partition Key required at least 1 character
                  </SbuxTranslate>
                ) : (
                  ""
                )
              }
            />
          </Box>

          <Box>
            <SbuxTextField
              label={<SbuxTranslate>Sort Key</SbuxTranslate>}
              handleChange={handleChange}
              value={table.sortKey}
              name={"sortKey"}
              preventAutoFocus
            />
          </Box>
        </Box>

        <Box className={classes.actionContainer}>
          <Button
            disabled={
              table.tableName.length === 0 || table.partitionKey.length === 0
            }
            variant="contained"
            size="medium"
            onClick={handleSubmit}
          >
            <SbuxTranslate>Create</SbuxTranslate>
          </Button>
          <Button variant="outlined" size="medium" onClick={handleClear}>
            <SbuxTranslate>Clear</SbuxTranslate>
          </Button>
        </Box>
      </Box>

      <Box sx={{ display: "flex", marginTop: 2, marginLeft: 40 }}>
        {openSnackbar && (
          <SbuxStackSnackbar
            open={openSnackbar}
            message={
              statusCode === 200 ? (
                <SbuxTranslate>Success! Your table was created</SbuxTranslate>
              ) : (
                message
              )
            }
            width={400}
            severity={statusCode === 200 ? "success" : "error"}
            autoHide={false}
            handleClose={handleClose}
          />
        )}
      </Box>
    </>
  );
};

export default AdminIvrTable;
